/* eslint-disable */
// useAnnouncementSend — the one place that calls send_notification() and turns
// its envelope into something a person can read.
//
// ⚠️ THE RPC NEVER THROWS (Convention #47). There is no exception to catch: it
// returns an envelope, and a refusal is data, not an error. A thrown error here
// means the TRANSPORT failed — the request never reached the function — which we
// treat as send_failed, because nothing was sent either way.
//
// ⚠️ {ok:true, recipients:0} IS NOT A SUCCESS. It means the send was accepted and
// reached nobody: every candidate was filtered by portal access or by muting.
// Reporting that as "Sent!" is the worst thing this feature can do, because the
// admin walks away believing the message landed. The caller is expected to keep
// the dialog open on `zero`.
//
// We send only the first six parameters. Severity and mandatory come from the
// type — a composer that can set severity is P5's control panel, not this.

import { useState, useRef } from 'react';
import { supabase } from '../supabase';

// Every code the deployed function can return, mapped to a sentence. Reading the
// raw code back to a user is not an error message, it is a shrug.
const MESSAGES = {
  unauthorized: 'Your session expired. Sign in again and retry.',
  unknown_type: 'This announcement type is switched off. Ask a super admin to enable it.',
  type_inactive: 'This announcement type is switched off. Ask a super admin to enable it.',
  system_source_forbidden: 'That type is sent by the system, not by people.',
  not_mandatory_eligible: 'This type cannot be made mandatory.',
  critical_requires_super_admin: 'Only a super admin can send a critical announcement.',
  bad_audience_kind: 'Pick who this goes to.',
  audience_not_permitted: 'You can only send to your own portal.',
  system_wide_requires_super_admin: 'Only a super admin can send a system-wide announcement.',
  portal_not_administered: 'You are not an admin of that portal.',
  audience_outside_portal: 'You can only send to your own portal.',
  send_failed: 'Something went wrong on the server. Nothing was sent.',
};

export function messageForError(envelope) {
  const code = envelope?.error;
  if (code === 'rate_limited') {
    const limit = envelope?.limit;
    return limit
      ? `Too many announcements in the last hour (limit ${limit}). Try again shortly.`
      : 'Too many announcements in the last hour. Try again shortly.';
  }
  if (code && MESSAGES[code]) return MESSAGES[code];
  // Not on the list — say so honestly and leave the envelope in the console for
  // whoever debugs it. `bad_severity` lands here by design: this composer never
  // passes a severity, so seeing it would mean something upstream changed.
  console.warn('[announcement] unexpected envelope from send_notification:', envelope);
  return 'Unexpected response from the server. Nothing was sent.';
}

export function useAnnouncementSend() {
  const [sending, setSending] = useState(false);
  // Guards the double-click. State alone is too slow: two clicks inside one
  // React batch both read `sending === false` and both fire.
  const inFlight = useRef(false);

  // `payload` carries ONLY {attachments:[{kind,id}]} — two facts per attachment
  // and nothing else. Never a code, never a name, and above all never an image
  // URL: the notification row is shared by every recipient, so a URL resolved
  // here under the SENDER's permissions would belong to everyone who reaches
  // the row, including the people the access filter excluded. Each reader
  // fetches the entity themselves instead (useAttachmentEntities).
  const send = async ({ typeKey, title, body, entityId, audienceKind, audienceSpec, payload }) => {
    if (inFlight.current) return { status: 'busy' };
    inFlight.current = true;
    setSending(true);

    try {
      const { data, error } = await supabase.rpc('send_notification', {
        p_type_key: typeKey,
        p_title: title,
        p_body: body || null,
        p_entity_id: entityId || null,
        p_audience_kind: audienceKind,
        p_audience_spec: audienceSpec || {},
        p_payload: payload || {},
      });

      // A transport failure. The function itself never throws, so this is the
      // request not arriving — nothing was sent.
      if (error) {
        console.warn('[announcement] transport failure:', error);
        return { status: 'error', message: MESSAGES.send_failed };
      }

      if (data?.error) return { status: 'error', message: messageForError(data), code: data.error };

      // Documented but unreachable from this composer: admin-source types are
      // never deduped. Handled so it can never render as `undefined`.
      if (data?.deduped) {
        return { status: 'deduped', message: 'An identical announcement already went out this hour.' };
      }

      if (data?.ok) {
        const recipients = Number(data.recipients ?? 0);
        const filtered = Number(data.filtered ?? 0);
        if (recipients === 0) return { status: 'zero', recipients: 0, filtered };
        return { status: 'sent', recipients, filtered, notificationId: data.notification_id };
      }

      return { status: 'error', message: messageForError(data) };
    } finally {
      inFlight.current = false;
      setSending(false);
    }
  };

  return { send, sending };
}
